import React, { Fragment, useEffect, useState, useMemo, useRef } from "react";

/* -------- packages -------- */
import { Row, Table, Button, Col, Card, CardBody, Modal, ModalHeader, ModalBody, Badge, Input, DropdownToggle, FormGroup, UncontrolledTooltip } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';
import Select from 'react-select';
import moment from "moment";
import { useTranslation } from "react-i18next";
import { rankItem } from '@tanstack/match-sorter-utils';
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
/* -------- ------- -------- */

/* -------- components -------- */
import DatePickerRange from "../DatePickerRange";
import ExcelExporter from "./ExcelExporter";
import IconListCell from "./IconListCell";
import IconAddListCell from "./IconAddListCell";
import CustomSelect from "./SelectRows";

import DynamicFormModal from "../DynamicFormModal";
import DeleteModal from "../DeleteModal";
import CommentsModal from "../Modals/CommentsModal";
import DocumentsModal from "../Modals/DocumentsModal";
import DuplicateModal from "../Modals/DuplicateModal";

import { avatarImages } from "../../../utils/items";
import TotalKpi from "../Charts&KPI/TotalKpi";
/* -------- ------- -------- */

/* -------- intern functions -------- */
import { getAutoSavePrefFromList, getConfigColumnFromList, hasAllRights, hasRight, hasUserBanRight, setAutoSavePrefForList, setConfigColumnFromList } from "../../../utils/userTools";
import { uploadDocumentRequest } from "../../../store/documents/actions";
import IconFormCell from "./IconFormCell";
import { updatePrefRequest } from "../../../store/actions";
import { get } from "../../../helpers/api_helper";
/* -------- ------- -------- */

const DebouncedInput = ({ value: initialValue, onChange, debounce = 500, ...props }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <input
      {...props}
      value={value}
      onChange={e => setValue(e.target.value)}
      className="searchBar form-control search-box me-2 mb-2 d-inline-block debounced-input"
    />
  );
};


function Filter ({ prefListName, column, filter, data, setColumnFilters, globalClearTriggered }) {
  const { t } = useTranslation();
  const user = useSelector(state => state.Login.user);
  const userPref = user && typeof user.prefs === 'object' ? user.prefs : JSON.parse(user.prefs);
  
  // Obtenir la valeur actuelle du filtre
  const columnFilterValue = column?.getFilterValue ? column.getFilterValue() : undefined;
  // Get the user preferences for this specific list
  const listPrefs = userPref && userPref?.lists?.find(list => list.id === prefListName) 
  const filters = listPrefs && listPrefs?.filters ? listPrefs.filters : [];
  
  // Utiliser une ref pour suivre si la valeur par défaut a déjà été appliquée
  const defaultApplied = useRef(false);
  const manuallyCleared = useRef(false);
  
  useEffect(() => {
    if (globalClearTriggered) {
      // Reset les refs quand le nettoyage global est déclenché
      defaultApplied.current = false;
      manuallyCleared.current = true; // Marquer comme supprimé manuellement
    }
  }, [globalClearTriggered]);
  
  // Appliquer la valeur par défaut seulement au chargement initial
  useEffect(() => {
    // First check if there's a saved filter in user preferences
    const savedFilter = filters.find(f => f.id === column?.id);
    
    if (savedFilter && !defaultApplied.current) {
      // Apply saved filter from user preferences
      defaultApplied.current = true;
      
      setColumnFilters(old => {
        return [...old.filter(f => f.id !== column?.id), { id: column?.id, value: savedFilter.value }];
      });
    } 
    else if (filter.defaultValue !== undefined && 
             columnFilterValue === undefined && 
             !defaultApplied.current && 
             !manuallyCleared.current && 
             !globalClearTriggered) {
      // Mark that the default value has been applied
      defaultApplied.current = true;
      
      if (['select', 'selectArray', 'selectIcon'].includes(filter.type)) {
        const defaultValues = Array.isArray(filter.defaultValue) 
          ? filter.defaultValue 
          : [filter.defaultValue];
          
        setColumnFilters(old => {
          return [...old.filter(f => f.id !== column?.id), { id: column?.id, value: defaultValues }];
        });
      } else if (filter.type === 'dateRangePicker' && typeof filter.defaultValue === 'object') {
        setColumnFilters(old => {
          return [...old.filter(f => f.id !== column?.id), { id: column?.id, value: filter.defaultValue }];
        });
      } else {
        setColumnFilters(old => {
          return [...old.filter(f => f.id !== column?.id), { id: column?.id, value: filter.defaultValue }];
        });
      }
    }
  }, [filter, column, columnFilterValue, setColumnFilters, filters, globalClearTriggered]);

  // Pour les filtres de type select, modifiez la fonction onChange pour permettre de supprimer complètement le filtre
  if (filter.type === 'select' || filter.type === 'selectArray' || filter.type === 'selectIcon') {
    const options = filter.options.map(option =>
      typeof option === 'object'
        ? { value: option.label, label: t(option.label) }
        : { value: option, label: t(option) }
    );
    return (
      <Select
        className="react-select-container"
        classNamePrefix="react-select"
        value={options.filter(option => (columnFilterValue || []).includes(option.value))}
        onChange={selected => {
          const selectedValues = selected ? selected.map(option => option.value) : [];
          setColumnFilters(old => {
            // Si aucune valeur n'est sélectionnée, supprimer complètement le filtre
            if (selectedValues.length === 0) {
              manuallyCleared.current = true;
              return old.filter(f => f.id !== column?.id);
            }
            manuallyCleared.current = false;
            return [...old.filter(f => f.id !== column?.id), { id: column?.id, value: selectedValues }];
          });
        }}
        options={options}
        isMulti
        placeholder={t(column?.columnDef.header)}
        data-cy={"filter-"+ filter.key}
        // Assurez-vous que le champ est effaçable
        isClearable={true}
      />
    );
  } else if (filter.type === 'dateRangePicker') {
    return (
      <DatePickerRange
        column={column}
        setColumnFilter={(columnId, value) =>
          setColumnFilters(old => {
            if (!value || (typeof value === 'object' && Object.keys(value).length === 0)) {
              manuallyCleared.current = true;
              return old.filter(f => f.id !== columnId);
            }
            manuallyCleared.current = false;
            return [...old.filter(f => f.id !== columnId), { id: columnId, value }];
          })
        }
        min={filter.min}
        max={filter.max}
      />
    );
  }

  // Filtre textuel par défaut
  return (
    <DebouncedInput
      type="text"
      value={columnFilterValue ?? ''}
      onChange={value => {
        if (value === '') {
          manuallyCleared.current = true;
        } else {
          manuallyCleared.current = false;
        }
        column.setFilterValue(value);
      }}
      placeholder={t(column.columnDef.header)}
      className="form-control"
    />
  );
}


const TableContainer = ({
  columnConfig,
  filterConfig,
  data,
  linkedEntityModel,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  pagination,
  paginationWrapper,
  isGlobalFilter,
  buttonName,
  buttonClass,
  prefListName,
  isAddButton,
  isNewButton = true,
  linkToNew,
  editFunction,
  detailFunction,
  deleteFunction,
  toggleSwitchFunction,
  banFunction,
  onlyDelete = false,
  noCheckbox = false,
  suffixId=0,
  withoutActions = false,
  actions = '',
  withEdit = true,
  withDelete = true,
  withDetail = false,
  withBan = false,
  editPopup = false,
  editPopupFields = null,
  coloredRowFunction,
  isModal = false,
  isEditPagePopup = false,
  isConfig = false,
  rowBackgroundColoring = false, 
  rowColoringAttribute = '',
  rowColoringFunction = null,
  isCatalog = false,
  totalKpi = null,
  defaultSortColumn = null,
  defaultSortDirection = 'asc',
  customDeleteMessage = null,
  customBulkDeleteMessage = null,
  editRights,
}) => {
  const user = useSelector(state => state.Login.user);
  const userPref = user && typeof user.prefs === 'object' ? user.prefs : JSON.parse(user.prefs);
  
  const columnInitials = getConfigColumnFromList(user, prefListName, columnConfig);
  const { t } = useTranslation();
  const [columnFilters, setColumnFilters] = useState(() => {
    // Initialiser avec les valeurs par défaut des filtres
    if (filterConfig && filterConfig.length > 0) {
      return filterConfig
        .filter(filter => filter.defaultValue !== undefined)
        .map(filter => {
          if (['select', 'selectArray', 'selectIcon'].includes(filter.type)) {
            const defaultValues = Array.isArray(filter.defaultValue) 
              ? filter.defaultValue 
              : [filter.defaultValue];
            return { id: filter.key, value: defaultValues };
          } else if (filter.type === 'dateRangePicker' && typeof filter.defaultValue === 'object') {
            return { id: filter.key, value: filter.defaultValue };
          } else {
            return { id: filter.key, value: filter.defaultValue };
          }
        });
    }
    return [];
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  useEffect(() => {
    // Si on a défini un filtre global par défaut
    const globalFilterDefault = filterConfig.find(filter => filter.key === 'global' && filter.defaultValue);
    if (globalFilterDefault && globalFilter === '') {
      setGlobalFilter(globalFilterDefault.defaultValue);
    }
  }, [filterConfig]);

  const handleChangePage = (value) => {
      table.setPageSize(Number(value))
  }

  const [savePref, setSavePref] = useState(() => {
    return getAutoSavePrefFromList(user, prefListName);
});
  
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const visibility = {};
    
    // Si l'autosave n'est pas activé, afficher toutes les colonnes par défaut
    const shouldUseStored = getAutoSavePrefFromList(user, prefListName);
    
    Object.keys(columnInitials).forEach(key => {
        let column = columnConfig.find((el) => el?.key == key);
        if (column && column.notInTab) {
            visibility[key] = false; // Les colonnes marquées notInTab restent cachées
        } else {
            // Si autosave désactivé, afficher la colonne, sinon utiliser les préférences
            visibility[key] = shouldUseStored ? columnInitials[key] : true;
        }
    });

    return visibility;
  });

  const [sorting, setSorting] = useState(() => {
    // Si une colonne de tri par défaut est spécifiée
    if (defaultSortColumn) {
      return [{
        id: defaultSortColumn,
        desc: defaultSortDirection === 'desc'
      }];
    }
    return [];
  });

  
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [localData, setLocalData] = useState(data); // Utiliser data comme état initial
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isIconModalOpen, setIsIconModalOpen] = useState(false);
  const [iconModalData, setIconModalData] = useState([]);
  const [modalTitle, setModalTitle] = useState("");

  const [isIconAddModalOpen, setIsIconAddModalOpen] = useState(false);
  const [iconAddModalData, setIconAddModalData] = useState([]);
  const [modalAddTitle, setModalAddTitle] = useState("");
  const [idSelectedModal, setIdSelectedModal] = useState(null);
  const [columnSelectedModal, setColumnSelectedModal] = useState([]);

  const [isIconFormModalOpen, setIsIconFormModalOpen] = useState(false);
  const [iconFormModalData, setIconFormModalData] = useState([]);
  const [modalFormTitle, setModalFormTitle] = useState("");
  const [iconFormFields, setIconFormModalFields] = useState([]);

  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [itemToDuplicate, setItemToDuplicate] = useState(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [initialValuesEditPopup, setInitialValuesEditPopup] = useState([]);

  // DOCUMENTS Modal State
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [documentsModalValues, setDocumentsModalValues] = useState([]);

  // COMMENTS Modal State
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [commentLinkedEntityId, setCommentLinkedEntityId] = useState('')

  // DOCUMENT UPLOAD State
  const [file, setFile] = useState(null);
  const [quoteDownloading, setQuoteDownloading] = useState(false);

  const [globalClearTriggered, setGlobalClearTriggered] = useState(false);


  /* -------------------- */

  const handleCommentsClick = (openModal, linkedEntityId) => {
    setIsCommentsModalOpen(openModal);
    setCommentLinkedEntityId(linkedEntityId);
  }

  const handleIconClick = (data, title) => {
    setIconModalData(data);
    setModalTitle(title);
    setIsIconModalOpen(true);
  };

  const handleIconAddClick = (data, title, id, column) => {
    setIconAddModalData(data);
    setModalAddTitle(title);
    setIsIconAddModalOpen(true);
    setIdSelectedModal(id);
    setColumnSelectedModal(column)
  };

  const handleIconFormClick = (data, title, fields) => {
    setIconFormModalData(data);
    setModalFormTitle(title);
    setIsIconFormModalOpen(true);
    setIconFormModalFields(fields);
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleEdit = (item) => {
    if(editPopup) {
      setInitialValuesEditPopup(item);
      setIsEditModalOpen(true);
    } else {
      dispatch(editFunction(item.id, navigate));
    }
  };

  const handleDelete = (item) => {
    dispatch(deleteFunction(item.id));
  };

  const handleDetail = (item) => {
    dispatch(detailFunction(item.id, navigate));
  };

  const handleBan = (item) => {
    let newItem = { ...item };
    newItem.isBan = !newItem.isBan;
    dispatch(banFunction(newItem, navigate));
  };

  const handleEditConfig = (item) => {
    navigate('/configurations/configurator/' + item.id);
  };

  const handleBulkDelete = () => {
    selectedRows.forEach(item => {
      handleDelete(item)
    });
    setSelectedRows([]);
    setIsBulkDeleteModalOpen(false);
  };

  const handleRowSelection = (row, isSelected) => {
    if (isSelected) {
      setSelectedRows(prev => [...prev, row]);
    } else {
      setSelectedRows(prev => prev.filter(r => r.id !== row.id));
    }
  };

  const handleUploadDocument = (doc) => {
    const newDocumentData = new FormData();
 
    newDocumentData.append('filedata', doc)
 
    if(doc.linkedEntityModel === 'ProjGroup') {
      const uploadedDoc = dispatch(uploadDocumentRequest(newDocumentData, {type: 'default', params: 'group'},  { "headers": { 'Content-Type': 'multipart/form-data' }}));
    }
    else{
      const uploadedDoc = dispatch(uploadDocumentRequest(newDocumentData, {type: 'projProduct_updatePrice', params: 'tmp'},  { "headers": { 'Content-Type': 'multipart/form-data' }}));
    }
  }

  const clearAllFilters = () => {
    setGlobalClearTriggered(true);
    setColumnFilters([]);
    setGlobalFilter('');
  };

  useEffect(() => {
    if (globalClearTriggered && columnFilters.length === 0) {
      setGlobalClearTriggered(false);
    }
  }, [columnFilters, globalClearTriggered]);

  useEffect(() => {
    setLocalData(data);
    if(idSelectedModal && columnSelectedModal && columnSelectedModal.key) {
      const newSelectedElement = data.find((el) => el.id == idSelectedModal);
      let newModalData = newSelectedElement[columnSelectedModal.key];
      setIconAddModalData(newModalData);
    }
  },[data])

  const canEdit = (hasRight(prefListName, 'w', user) || hasRight(prefListName, "pwd", user)) && withEdit;
  const canDelete = hasRight(prefListName, 'd', user) && withDelete;
  const canDetail = hasRight(prefListName, 'r', user) && withDetail;
  const canBan = hasRight(prefListName, 'ban', user) && withBan;

  const columns = useMemo(() => {
    const checkbox = (!noCheckbox) ? [{
      header: () => (
        <div style={{ 
          margin: 0, 
          padding: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '24px'
        }}>
          {selectedRows.length > 0 && (
            <div
              className="text-danger"
              onClick={() => setIsBulkDeleteModalOpen(true)}
              style={{
                cursor: 'pointer',
                padding: 0,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0
              }}
            >
              <i 
                className="mdi mdi-delete" 
                style={{ 
                  fontSize: '20px',
                  margin: 0,
                  padding: 0,
                  lineHeight: 1
                }} 
              />
            </div>
          )}
        </div>
      ),
      accessorKey: 'select',
      type: 'select',
      enableSorting: false,
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={selectedRows.some(r => r.id === row.original.id)}
          onClick={e => { handleRowSelection(row.original, e.target.checked) }}
          onChange={() => console.log()}
          style={{
            margin: '0 auto',
            display: 'block'
          }}
        />
      ),
    }] : null;
    let columnsWithActions = (checkbox) ? checkbox : [];
    columnsWithActions = columnsWithActions.concat([
      ...columnConfig.map(column => ({
        header: t(column.key),
        accessorKey: column.key,
        type: column.type,
        enableColumnFilter: true,
        enableSorting: column.type !== 'gps',
        sortDescFirst: false,
        enableGlobalFilter: true,
        enableFilters: true,
        
        cell: (cell) => {
          if (column.type === 'date' || column.type === 'dateRangePicker') {
            return <span className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} >{moment(cell.getValue()).format("DD/MM/YYYY")}</span>;
          } else if (column.type === 'iconTab') {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i
                  className={`${column.icon} position-relative`}
                  onClick={() => (cell.getValue() && cell.getValue().length && cell.getValue().length > 0) && handleIconClick(cell.getValue(), cell.row.original[column.libElement])}
                  style={{ cursor: (cell.getValue() && cell.getValue().length && cell.getValue().length > 0) && 'pointer', fontSize: 20 }}
                >
                    <Badge
                      color={(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?"primary":"secondary"}
                      pill
                      className="position-absolute top-0 start-70"
                      style={{ fontSize: '0.70rem' }}
                    >
                      {(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?cell.getValue().length:0}
                    </Badge>
                </i>
              </div>
            );
          } else if (column.type === 'iconTabAdd') {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i
                  className={`${column.icon} position-relative`}
                  onClick={() => handleIconAddClick(cell.getValue(), cell.row.original[column.libElement], cell.row.original.id, column)}
                  style={{ cursor: 'pointer', fontSize: 20 }}
                >
                    <Badge
                      color={(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?"primary":"secondary"}
                      pill
                      className="position-absolute top-0 start-70"
                      style={{ fontSize: '0.70rem' }}
                    >
                      {(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?cell.getValue().length:0}
                    </Badge>
                </i>
              </div>
            );
          } else if (column.type === 'iconTabComments') {
            return (
              <div className="tableCellContainer">
                <i 
                  className={`mdi mdi-comment-text-multiple-outline`}
                  onClick={ ()=> {handleCommentsClick(true, cell.row.original.id)}}
                  style={{ cursor: 'pointer', fontSize: 24 }}
                  >
                  <span className="badge rounded-pill bg-danger badgeIconCustom customDynamicBg">
                    {(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?cell.getValue().length:0}
                  </span>
                </i>
              </div>
            );
          } else if (column.type === 'iconTabDocuments') {
            return (
              <div className="tableCellContainer">
                <i 
                  className={`mdi mdi-file-document-multiple-outline`}
                  onClick={ ()=> {setIsDocumentsModalOpen(true); setDocumentsModalValues(cell.getValue())}}
                  style={{ cursor: 'pointer', fontSize: 24 }}
                  >
                  <span className="badge rounded-pill bg-danger badgeIconCustom customDynamicBg">
                    {(cell.getValue() && cell.getValue().length && cell.getValue().length > 0)?cell.getValue().length:0}
                  </span>
                </i>
              </div>
            );
          } else if (column.type === 'iconForm') {
            return (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i
                  className={`${column.icon} position-relative`}
                  onClick={() => (column.fields && column.fields.length > 0) && handleIconFormClick(cell.row.original, cell.row.original[column.libElement], column.fields)}
                  style={{ cursor:'pointer', fontSize: 20 }}
                >
                </i>
              </div>
            );
          } else if  (column.type === 'docName') {
              return (
                <span className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} >{t(cell.getValue())}</span>
              );
          } else if (column.type === 'switch') {
            const isCurrentUser = user && cell.row.original.id === user.id;
            return (
              <FormGroup switch>
                <Input
                  type="switch"
                  role="switch"
                  onClick={() => column.toggleFunction(cell.row.original)}
                  onChange={() => console.log()}
                  checked={cell.getValue()}
                  disabled={(column.key === "isBan" && (canBan || isCurrentUser))}
                  className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} 
                />
              </FormGroup>
            );
          } else if (column.type === 'array') {
            return (
              <div className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} >
                {cell.getValue()?.map((obj, index) => (
                  <div key={index}>{obj}</div>
                ))}
              </div>
            );
          } else if (column.type === 'gps') {
            const longitude = cell.row.original.longitude;
            const latitude = cell.row.original.latitude;
            return <span className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} >{`X: ${longitude} - Y: ${latitude}`}</span>;
          } else if (column.type === 'tag') {
            if(column.colorFunction) {
              return <Badge className={"font-size-15 badge-soft-" +
                ((column.colorKey && cell.row.original[column.colorKey]) ? cell.row.original[column.colorKey] : column.colorFunction(cell.getValue()) ? column.colorFunction(cell.getValue()) : '')}
              >
                {(cell.getValue())?(cell.getValue()) + '%':'NA'}
              </Badge>;
            } else {
              return <Badge className={"font-size-13 badge-soft-" +
                (column.colorOptions[cell.getValue()] ? column.colorOptions[cell.getValue()] : '')}
              >
                {t(cell.getValue())}
              </Badge>;
            }
          } else if (column.type === 'validationButton') {
            return (
              <div className="d-flex justify-content-center">
                {cell.getValue() === true ? (
                  <i className="bx bx-check-circle text-success" style={{ fontSize: "1.2rem" }}></i>
                ) : (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => column.function && column.function(cell.row.original)}
                  >
                    {t('validate')}
                  </Button>
                )}
              </div>
            );
          } else if (column.type === 'validationIcon') {
            return (
              <div className="d-flex justify-content-center">
                {cell.getValue() === true ? (
                  <i className="bx bx-check-circle text-success" style={{ fontSize: "1.2rem" }}></i>
                ) : (
                  <i className="bx bx-x-circle text-warning" style={{ fontSize: "1.2rem" }}></i>
                )}
              </div>
            );
          } else if (column.type === 'banIcon') {
            return (
              <div className="d-flex justify-content-center">
                {cell.getValue() !== true ? (
                  <i className="bx bx-check-circle text-success" style={{ fontSize: "1.2rem" }}></i>
                ) : (
                  <i className="bx bx-x-circle text-danger" style={{ fontSize: "1.2rem" }}></i>
                )}
              </div>
            );
          } else if (column.type === 'link') {
            // Construire l'URL du lien en ajoutant le champ spécifié
            const linkUrl = column.link + (column.fieldAddedToLink ? cell.row.original[column.fieldAddedToLink] : '');
            
            // Préparer l'état pour la navigation si linkState est défini
            const navigationState = column.linkState ? {
              searchValue: cell.row.original[column.linkState.searchValue] // Utilise la valeur du champ spécifié
            } : null;
            
            return (
              <Link 
                to={linkUrl}
                state={navigationState} 
                className={(coloredRowFunction) ? coloredRowFunction(cell.row.original) + " link-column text-decoration-underline" : "link-column text-decoration-underline"}
                style={{ 
                  color: '#007bff',
                  textDecoration: 'none',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = 'underline';
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = 'none';
                }}
              >
                {cell.getValue() !== undefined && cell.getValue() !== null ? cell.getValue() : ''}
              </Link>
            );
          } else {
            if (column.isIconProfile) {
              const cellValue = cell.getValue();
              let currentImage;
          
              // Vérifier si la valeur a déjà une image associée
              currentImage = avatarImages.find((image) => image.id === cell.row.original.groupId)?.path;
          
              const imgSrc = (currentImage) ? currentImage : avatarImages[0].path;
              if(imgSrc) {
                return (
                  <span>
                    <img
                      className="rounded-circle header-profile-user"
                      src={imgSrc}
                      style={{ width: '30px', marginRight: '10px' }}
                    />
                    {cellValue}
                  </span>
                );
              } else {
                <span className={(coloredRowFunction)?coloredRowFunction(cell.row.original):''} >{cell.getValue()}</span>;
              }
              
            } else {
              // Ajout des adornments (décorations de début et de fin)
              const startAdornment = column.startAdornment ? <span className="adornment start-adornment">{column.startAdornment}</span> : null;
              const endAdornment = column.endAdornment ? <span className="adornment end-adornment">{column.endAdornment}</span> : null;

              return (
                <span className={(coloredRowFunction)?coloredRowFunction(cell.row.original) : '' + (column.columnClasses ? ' ' + column.columnClasses : '')}>
                  {startAdornment}
                  {cell.getValue() !== undefined && cell.getValue() !== null ? t(cell.getValue()) : ''}
                  {endAdornment}
                </span>
              );
            }
          }
        },
        filterFn: column.type === 'iconTab' ? 'iconList' : (filterConfig.filter((el) => el.type == 'select' && el.key == column.key)) ? 'includes' : column.type === 'array' ? 'array' : column.type === 'date' ? 'dateRange' : 'fuzzy',
      })),
    ]);

    if (((canEdit || canDelete || canDetail || canBan)) && actions == 'documents') {
      columnsWithActions.push({
        header: t('actions'),
        accessorKey: 'actions',
        type: 'actions',
        cell: (cell) => {
          let newCanEdit = canEdit && !onlyDelete && ((prefListName == 'user') ? (hasAllRights(cell.row.original.groupRights, user) || hasRight(prefListName, 'pwd', user)) : true);
          let newCanDelete = canDelete && ((prefListName == 'user') ? hasAllRights(cell.row.original.groupRights, user) : true);
          let newCanDetail = canDetail && !onlyDelete;
          let newCanBan = canBan && ((user && cell.row.original.id !== user.id) || !user);
          return (
          <div className="d-flex gap-1">
            {newCanEdit && (
              <div
                id= {"projEditBox"+cell.row.original.id}
                className="text-info listActionIcon"
                onClick={() => console.log()}
                onChange={() => console.log()}
                sx={{
                  ":hover": {
                    cursor: 'pointer',
                  },
                }}
              >
                <i className='bx bx-download'></i>
              </div>
            )}
            {newCanDelete && (
              <div
                id={"projDeleteBox"+cell.row.original.id}
                className="text-danger listActionIcon"
                onClick={() => {
                  if (canDelete) {
                  setIsDeleteModalOpen(true);
                    setItemToDelete(cell.row.original);
                  }
                }}
                onChange={() => console.log()}
                sx={{
                  ":hover": {
                    cursor: 'pointer',
                  },
                }}
              >
                <i className="bx bx-trash" />
              </div>
            )}
            {newCanBan && (
              <div
                id={"projBanBox"+cell.row.original.id}
                className={(cell.row.original.isBan)?"text-success listActionIcon":"text-danger listActionIcon"}
                onClick={() => {
                  if (canBan) {
                    handleBan(cell.row.original);
                  }
                }}
                onChange={() => console.log()}
                sx={{
                  ":hover": {
                    cursor: 'pointer',
                  },
                }}
              >
                {cell.row.original.isBan ? (
                  <i className="mdi mdi-location-enter" />
                ) : (
                  <i className="mdi mdi-minus-circle-outline" />
                )}
              </div>
            )}
          </div>
        )}
      });
    }

    if (((canEdit || canDelete || canDetail || canBan)) && !withoutActions) {
      columnsWithActions.push({
        header: t('actions'),
        accessorKey: 'actions',
        type: 'actions',
        cell: (cell) => {
          let newCanEdit = canEdit && !onlyDelete && ((prefListName == 'user') ? (hasAllRights(cell.row.original.groupRights, user) || hasRight(prefListName, 'pwd', user)) : true);
          let newCanDelete = canDelete && ((prefListName == 'user') ? hasAllRights(cell.row.original.groupRights, user) : true);
          let newCanDetail = canDetail && !onlyDelete;
          let newCanBan = canBan && ((user && cell.row.original.id !== user.id) || !user);
          
          // Détermine si les actions sont désactivées
          const isDisabled = isConfig && cell.row.original?.ProjProject?.status == "CLOSED";
          const isQuoteOk = isConfig && cell.row.original?.status == "QUOTE_OK";
          const isQuoteExpired = isConfig && cell.row.original?.status == "QUOTE_EXPIRED";
          
          return (
          <div className="d-flex gap-3">
            {newCanDetail && (
              <>
              <div
                id= {"projDetailBox"+cell.row.original.id}
                className={!isDisabled ? "text-info listActionIcon" : "text-secondary listActionIcon"}
                onClick={() => !isDisabled && canDetail && handleDetail(cell.row.original)}
                onChange={() => console.log()}
                style={{
                  cursor: !isDisabled ? 'pointer' : 'default'
                }}
                >
                <i className="mdi mdi-file-eye-outline font-size-18" />
              </div>
              {!isDisabled && (
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projDetailBox"+cell.row.original.id}
                  >
                  {t('details')}
                </UncontrolledTooltip>
              )}
              </>
            )}
            {newCanEdit && (
              <>
              <div
                id= {"projEditBox"+cell.row.original.id}
                className={!(isDisabled || isQuoteExpired || isQuoteOk) ? "text-info listActionIcon" : "text-secondary listActionIcon"}
                onClick={() => {
                  if (!(isDisabled || isQuoteExpired || isQuoteOk) && canEdit) {
                    if((editRights && editRights(cell.row.original)) || !editRights) {
                      handleEdit(cell.row.original);
                    }
                  }
                }}
                onChange={() => console.log()}
                style={{
                  cursor: !(isDisabled || isQuoteExpired || isQuoteOk) ? 'pointer' : 'default'
                }}
                >
                <i className="bx bx-edit-alt" />
              </div>
              {!(isDisabled || isQuoteExpired || isQuoteOk) && (
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projEditBox"+cell.row.original.id}
                  >
                  {t('edit')}
                </UncontrolledTooltip>
              )}
              </>
            )}
            {isConfig && (
              <>
              <div
                id= {"projDuplicateBox"+cell.row.original.id}
                className={!isDisabled ? "text-info listActionIcon" : "text-secondary listActionIcon"}
                onClick={() => {
                  if (!isDisabled && canEdit) {
                    if((editRights && editRights(cell.row.original)) || !editRights) {
                      setItemToDuplicate(cell.row.original);
                      setIsDuplicateModalOpen(true);
                    }
                  }
                }}
                onChange={() => console.log()}
                style={{
                  cursor: !isDisabled ? 'pointer' : 'default'
                }}
              >
                <i className='mdi mdi-content-duplicate'></i>
              </div>
              {!isDisabled && (
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projDuplicateBox"+cell.row.original.id}
                  >
                  {t('duplicateConfig')}
                </UncontrolledTooltip>
              )}
              </>
            )}
            {isConfig && (
              <>
              <div
                id= {"projEditConfigBox"+cell.row.original.id}
                className={"text-info listActionIcon"}
                onClick={() => {
                    handleEditConfig(cell.row.original);
                }}
                onChange={() => console.log()}
                style={{
                  cursor: 'pointer'
                }}
                >
                <i className='bx bx-sitemap'></i>
              </div>
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projEditConfigBox"+cell.row.original.id}
                  >
                  {t('editConfig')}
                </UncontrolledTooltip>
              </>
            )}
            {isConfig && (
              <>
              <div
                id= {"projExportBox"+cell.row.original.id}
                className={(isQuoteOk) ? "text-success listActionIcon" : "text-secondary listActionIcon"}
                onClick={() => {
                  if (cell.row.original.quoteUrl) {
                    get(cell.row.original.quoteUrl, { responseType: 'blob' })
                      .then((response) => {
                        setQuoteDownloading(true);

                        const linkId = 'quote-export-download-link';
                        const existing = document.getElementById(linkId);
                        if (existing) existing.remove();

                        const url = window.URL.createObjectURL(new Blob([response]));
                        const link = document.createElement('a');
                        link.id = linkId;
                        link.href = url;
                        link.setAttribute('download', cell.row.original.quoteUrl.split('/').pop());
                        document.body.appendChild(link);
                        link.click();
                        if(existing) existing.remove();
                        setQuoteDownloading(false);
                      })
                  }
                }}
                onChange={() => console.log()}
                style={{
                  cursor: (isQuoteOk) ? 'pointer' : 'default'
                }}
                >
                {quoteDownloading ?
                  <i className="bx bx-loader-alt bx-spin" /> 
                  :
                  <i className="bx bx-file" />
                }
              </div>
              {(isQuoteOk) && ( 
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projExportBox"+cell.row.original.id}
                  >
                  {t('exportFile')}
                </UncontrolledTooltip>
              )}
              {(isQuoteExpired) && ( 
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projExportBox"+cell.row.original.id}
                  >
                  {t('exportFileExpired')}
                </UncontrolledTooltip>
              )}
                
              </>
            )}
            {newCanDelete && (
              <>
              <div
                id={"projDeleteBox"+cell.row.original.id}
                className={"text-danger listActionIcon"}
                onClick={() => {
                  if (canDelete) {
                    setIsDeleteModalOpen(true);
                    setItemToDelete(cell.row.original);
                  }
                }}
                onChange={() => console.log()}
                style={{
                  cursor: 'pointer'
                }}
              >
                <i className="bx bx-trash" />
              </div>
                <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projDeleteBox"+cell.row.original.id}
                  >
                  {t('delete')}
                </UncontrolledTooltip>
              </>
            )}
            {newCanBan && (
              <>
              <div
                id={"projBanBox"+cell.row.original.id}
                className={(cell.row.original.isBan)?"text-success listActionIcon":"text-danger listActionIcon"}
                onClick={() => {
                  if (canBan) {
                    handleBan(cell.row.original);
                  }
                }}
                onChange={() => console.log()}
                style={{
                  cursor: 'pointer'
                }}
              >
                {cell.row.original.isBan ? (
                  <i className="mdi mdi-location-enter" />
                ) : (
                  <i className="mdi mdi-minus-circle-outline" />
                )}
              </div>
              <UncontrolledTooltip
                  transition={{ timeout: 300 }}
                  placement="top"
                  fade={false}
                  target={"projBanBox"+cell.row.original.id}
                  >
                  {(cell.row.original.isBan)?t('unban'):t('ban')}
              </UncontrolledTooltip>
              </>
            )}
          </div>
        )}
      });
    }

    return columnsWithActions;
  }, [data, selectedRows, localData, columnFilters, columnVisibility]);
  
  const globalFilterSearch = (row, columnId, value, addMeta) => {
    const rowValue = row.getValue(columnId);
    let itemRank = { passed: false };

    const isMatch = (text, searchValue) => {
      if (!text || !searchValue) return false;
      
      const textStr = String(text).toLowerCase();
      const searchStr = String(searchValue).toLowerCase();
      
      return textStr.includes(searchStr);
    };

    // Fonction pour détecter si une valeur est vraiment une date
    const isRealDate = (val) => {
      // Exclure les nombres purs (comme les références)
      if (typeof val === 'number' || /^\d+$/.test(val)) {
        return false;
      }
      
      // Exclure les strings qui ressemblent à des références (chiffres + lettres)
      if (typeof val === 'string' && /^[A-Za-z0-9-]+$/.test(val)) {
        return false;
      }
      
      // Vérifier si c'est une date valide avec un format de date reconnaissable
      const momentDate = moment(val);
      if (!momentDate.isValid()) return false;
      
      // Vérifier que ce n'est pas une date par défaut
      if (momentDate.format("DD/MM/YYYY") === '01/01/1970') return false;
      
      // Vérifier que la valeur originale ressemble à une date
      // (contient des séparateurs de date ou est un timestamp Unix valide)
      if (typeof val === 'string') {
        return /[\/-]/.test(val) || /^\d{4}-\d{2}-\d{2}/.test(val) || /^\d{2}\/\d{2}\/\d{4}/.test(val);
      }
      
      // Si c'est un objet Date
      if (val instanceof Date) return true;
      
      return false;
    };

    const compareValue = (val) => {
      if (!val) return false;
      
      // Gestion des vraies dates seulement
      if (isRealDate(val)) {
        const formattedDate = moment(val).format("DD/MM/YYYY");
        return isMatch(formattedDate, value);
      } 
      
      // Recherche directe sur la valeur
      if (isMatch(val, value)) {
        return true;
      }
      
      // Recherche sur la valeur traduite seulement si différente de la valeur originale
      const translatedValue = t(val);
      if (translatedValue !== val && isMatch(translatedValue, value)) {
        return true;
      }
      
      return false;
    };

    // Gestion des tableaux
    if (Array.isArray(rowValue)) {
      for (const item of rowValue) {
        if (typeof item === 'string' || typeof item === 'number') {
          if (compareValue(item)) {
            itemRank.passed = true;
            addMeta({ itemRank });
            return true;
          }
        } else if (typeof item === 'object' && item !== null) {
          if (item.lib && compareValue(item.lib)) {
            itemRank.passed = true;
            addMeta({ itemRank });
            return true;
          }
        }
      }
    } else {
      if (compareValue(rowValue)) {
        itemRank.passed = true;
        addMeta({ itemRank });
        return true;
      }
    }

    addMeta({ itemRank });
    return false;
  };
  
  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };

  const dateRangeFilter = (row, columnId, filterValue) => {
    const rowDate = moment(row.getValue(columnId));
    const { start, end } = filterValue;
    return rowDate >= moment(start) && rowDate <= moment(end);
  };

  const includeStringFilter = (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (Array.isArray(filterValue)) {
      return filterValue.includes(value);
    }
    return false;
  };

  const arrayFilter = (row, columnId, filterValue) => {
    const rowValues = row.getValue(columnId);
    return filterValue.some(filterVal => {
      return rowValues && rowValues.includes(filterVal);
    });
  };

  const iconListSort = (row, columnId, filterValue) => {
    const filter = filterConfig.find(el => el.key == columnId);
    const rowValues = row.getValue(columnId);
    return filterValue.some(filterVal => {
      return rowValues && filter && filter.sortedKey && rowValues.find(el => el[filter.sortedKey] == filterVal);
    });
  };

  const table = useReactTable({
    columns,
    data: localData, // Utiliser localData pour la table
    filterFns: {
      fuzzy: fuzzyFilter,
      includes: includeStringFilter,
      dateRange: dateRangeFilter,
      array: arrayFilter,
      iconList: iconListSort,
      globalFilterSearch: globalFilterSearch,
    },
    state: {
      columnFilters,
      globalFilter,
      columnVisibility,
      sorting
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    globalFilterFn: globalFilterSearch,
    getColumnCanGlobalFilter: (c) => {
      return c.columnDef.enableGlobalFilter
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState
  } = table;

  const filtersColumns = columnFilters.map((filter) => {
      return {
        id: filter.id,
        value: filter.value,
      }
  });

  useEffect(() => {
    const filteredPref = setAutoSavePrefForList(user, prefListName, savePref);
    dispatch(updatePrefRequest(user.id, {prefs: filteredPref}));
  }, [savePref])

  useEffect(() => {
    // Sauvegarder les colonnes seulement si l'auto-save est activé
    if (savePref) {
        const filteredPref = setConfigColumnFromList(user, prefListName, columnVisibility, filtersColumns, savePref);
        dispatch(updatePrefRequest(user.id, {prefs: filteredPref}));
    }
}, [columnVisibility, columnFilters]);

  const handleColumnToggle = (columnId) => {
    const updatedCol = {};
    updatedCol[columnId] = !columnVisibility[columnId];
    let newCols = {...columnVisibility,...updatedCol};
    setColumnVisibility((prev) => ({
        ...prev,
        [columnId]: !prev[columnId]
    }));
    
    // Sauvegarder seulement si autoSave est activé
    if (savePref) {
        const filteredPref = setConfigColumnFromList(user, prefListName, newCols, filtersColumns, savePref);
        dispatch(updatePrefRequest(user.id, {prefs: filteredPref}));
    }
};

  return (
    <Fragment>
      <Row>
        <Col lg="12">
        { !isModal &&
          
          <Col xl={12} className={"header-kpi d-flex align-items-center flex-wrap" + (totalKpi ? " justify-content-between" : " justify-content-end") }>
            { (totalKpi) &&

              <TotalKpi label={totalKpi.label} 
                        iconClass={totalKpi.icon}
                        rightName={prefListName}
                        description={(data && data.length)?data.length:0} />
            }
            {isAddButton && hasRight(prefListName, 'c', user) && (
              <Button id={"projAddBtn"+suffixId} type="button" className={buttonClass} onClick={() => (isEditPagePopup)?editFunction():navigate(linkToNew)} data-cy={"add-button-"+ buttonName}>
                <i className="mdi mdi-plus me-1"></i> {(isNewButton)?t('new') + ' ' + t(buttonName):t(buttonName)}
              </Button>
            )}
          </Col>
          }
          <Card className="tableBody">
            <CardBody>
              <Row className="mb-2">
                <Col lg={11} className="d-flex flex-wrap">
                  {filterConfig.map((filter, index) => (
                    <Col sm={4} key={index} className="pe-2 mb-2">
                      <Filter
                        prefListName={prefListName}
                        column={table.getAllColumns().find(col => col.id === filter.key)} 
                        filter={filter} 
                        data={localData} 
                        setColumnFilters={setColumnFilters}
                        columnFilters={columnFilters}
                        globalClearTriggered={globalClearTriggered} // Ajoutez cette ligne
                      />
                    </Col>
                  ))}
                  {isGlobalFilter && (
                    <Col sm={4} className="pe-2 mb-2">
                      <DebouncedInput
                        id={"projSearchInput"}
                        key={"projSearchInput" + suffixId}
                        value={globalFilter ?? ''}
                        onChange={value => setGlobalFilter(String(value))}
                        placeholder={t('search')}
                        data-cy={"search-input"}
                      />
                    </Col>
                  )}
                </Col>
                {(filterConfig.length > 0 || isGlobalFilter) && (
                  <Col lg={1} className="d-flex justify-content-center">
                    <Button
                      type="button"
                      id={"projClearFiltersBtn" + suffixId}
                      className="btn btn-danger h-100 d-flex align-items-center justify-content-center"
                      onClick={clearAllFilters}
                      style={{
                        maxHeight: '38px',
                      }}
                      data-cy={"clear-filters-button"}
                    >
                      <i className="mdi mdi-filter-remove" style={{ fontSize: '18px' }}></i>
                    </Button>
                    <UncontrolledTooltip
                      transition={{ timeout: 300 }}
                      placement="top"
                      fade={false}
                      target={"projClearFiltersBtn" + suffixId}
                    >
                      {t('clearFilters')}
                    </UncontrolledTooltip>
                  </Col>
                )}
              </Row>
              <Row className="d-flex mb-2 justify-content-between">
                <div className="groupTopList">

                <Col className="groupTopListBtns">
                  <div className="me-1">
                    <Button
                      type="button"
                      id={"projColumnBtn"+suffixId}
                      className={`btnTable`}
                      onClick={() => setIsColumnModalOpen(true)}
                      data-cy={"table-button-"+ buttonName + "s-header"}
                    >
                      <i className="mdi mdi-table-headers-eye iconOptions"></i>
                    </Button>
                    <UncontrolledTooltip
                        transition={{ timeout: 300 }}
                        fade={false}
                        placement="top"
                        target={"projColumnBtn"+suffixId}
                        >
                        {t('manageColumns')}
                    </UncontrolledTooltip>
                  {actions != 'documents' &&
                  <>
                  <ExcelExporter suffixId={suffixId} columns={columns} visibleColumns={Object.keys(columnVisibility).filter(colKey => columnVisibility[colKey])} data={getRowModel().rows.map(row => row.original)} moduleName={prefListName} columnFilters={columnFilters} globalFilter={globalFilter}/>
                  </>

                  
                  }
                  </div>

                  {isCatalog && (
                    <div className="me-1">
                    
                    <Button
                      type="button"
                      id={"projUploadPriceBtn"+suffixId}
                      className={`btnTable`}
                      onClick={() => setIsImportModalOpen(true)}
                      data-cy={"table-button-"+ buttonName + "s-header"}
                    >
                      <i className="mdi mdi-currency-eur iconOptions"></i>
                    </Button>
                    <UncontrolledTooltip
                        transition={{ timeout: 300 }}
                        placement="top"
                        fade={false}
                        target={"projUploadPriceBtn"+suffixId}
                        >
                        {t('importPrice')}
                    </UncontrolledTooltip>
                  </div>
                  )}

                  

                  {actions != 'documents' &&
                    <div className="me-1 autoSaveBtns">

                      <Button
                        type="button"
                        id={"projSavePrefColumnBtn"+suffixId}
                        className={`savePrefBtn` + (savePref === false ? ' customDynamicColor': '')}
                        data-cy={"table-button-"+ buttonName + "s-save-off"}
                        >
                        <i className="mdi mdi-content-save-off-outline iconOptions"></i>
                      </Button>
                        <FormGroup switch className="switchContainer" id={'projSavePrefColumnSwitch'+suffixId}>
                          <Input 
                          type="switch"
                          checked={savePref}
                          onChange={() => {setSavePref(!savePref)}}
                          />
                        </FormGroup>
                        <UncontrolledTooltip
                            transition={{ timeout: 300 }}
                            placement="top"
                            fade={false}
                            target={"projSavePrefColumnSwitch"+suffixId}
                            >
                            {t('savePreferences')} : {t(savePref)}
                        </UncontrolledTooltip>
                      <Button
                        type="button"
                        id={"projSavePrefColumnBtn"+suffixId}
                        className={`savePrefBtn` + (savePref === true ? ' customDynamicColor': '')}
                        data-cy={"table-button-"+ buttonName + "s-save-on"}
                      >
                        <i className="mdi mdi-content-save-outline iconOptions"></i>
                      </Button>
                    
                    </div>
                  }
                </Col>
                <Col className="selectRow">
                  <CustomSelect
                    func={{handleChangePage}}
                    title={t('show')}
                    options={[10, 25, 50, 100]}
                  />
                </Col>
                </div>
              </Row>
              <div className={divClassName ? divClassName : "table-responsive"}>
                <Table hover className={tableClass} style={{fontSize: 12}} bordered={isBordered} id={"projTable"+suffixId}>
                  <thead className={theadClass}>
                    {getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} data-cy={"table-header"}>
                        {headerGroup.headers.map(header => {
                          return (
                          <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className={header.column.columnDef.enableSorting && header.id !== 'actions' ? "sorting sorting_desc" : ""}
                          style={{ cursor: header.column.getCanSort() && header.id !== 'actions' ? "pointer" : "auto" }}
                          onClick={header.column.getCanSort() && header.id !== 'actions' ? header.column.getToggleSortingHandler() : undefined}
                          >
                            {header.isPlaceholder ? null : (
                              <React.Fragment>
                                <div
                                  className={header.column.getCanSort() && header.id !== 'actions' ? "cursor-pointer select-none" : ""}
                                >
                                  {flexRender(header.column.columnDef.header, header.getContext())}
                                  {header.column.columnDef.enableSorting && header.id !== 'actions' && (
                                    <>
                                      {{
                                        asc: <i className="mdi mdi-arrow-down"></i>,
                                        desc: <i className="mdi mdi-arrow-up"></i>,
                                      }[header.column.getIsSorted()] ?? null}
                                    </>
                                  )}
                                </div>
                              </React.Fragment>
                            )}
                          </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {getRowModel().rows.map(row => {
                      const rowStyle = {};
                      if (rowBackgroundColoring && rowColoringFunction) {
                        const backgroundColor = rowColoringFunction(row.original);
                        if (backgroundColor) {
                          rowStyle.backgroundColor = backgroundColor;
                        }
                      }
                      return (
                        <tr key={row.id} data-cy={"table-row-" + row.id} >
                          {row.getVisibleCells().map(cell => {
                            return (
                              <td key={cell.id} style={rowStyle}>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
                  
              {isPagination && (
                  <Row>
                    <Col sm={12} md={5}>
                      <div className="dataTables_info">
                        {t("showing")} {getRowModel().rows.length} {t("on")} {table.getFilteredRowModel().rows.length} {t("results")}
                      </div>
                    </Col>
                    <Col sm={12} md={7}>
                      <div className={paginationWrapper}>
                        <ul className={pagination}>
                          <li className={`paginate_button page-item previous ${!getCanPreviousPage() ? "disabled" : ""}`}>
                            <Link to="#" className="page-link" onClick={previousPage}><i className="mdi mdi-chevron-left"></i></Link>
                          </li>
                            {getPageOptions().length > 5 ? (
                              <>
                                {/* First page if not on it */}
                                {getState().pagination.pageIndex > 2 && (
                                  <li className="paginate_button page-item">
                                    <Link to="#" className="page-link" onClick={() => setPageIndex(0)}>1</Link>
                                  </li>
                                )}

                                {/* Show ellipsis if not close to first page */}
                                {getState().pagination.pageIndex > 2 && (
                                  <li className="paginate_button page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                )}

                                {/* Show pages around current page */}
                                {getPageOptions()
                                  .filter(item => 
                                    item >= getState().pagination.pageIndex - 2 && 
                                    item <= getState().pagination.pageIndex + 2
                                  )
                                  .map((item, key) => (
                                    <li key={key} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active " : ""}`}>
                                      <Link to="#" className={`page-link ${getState().pagination.pageIndex === item ? " customDynamicBg active" : ""}`} onClick={() => setPageIndex(item)}>{item + 1}</Link>
                                    </li>
                                  ))}
                                 {/* Show ellipsis if the current page index is far from the last page */}
                                {getState().pagination.pageIndex < getPageOptions().length - 3 && (
                                  <li className="paginate_button page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                )}
                                {/* Show the last page link if the current page index is far from the last page */}
                                {getState().pagination.pageIndex < getPageOptions().length - 3  && (
                                  <li className="paginate_button page-item">
                                    <Link to="#" className="page-link" onClick={() => setPageIndex(getPageOptions().length - 1)}>{getPageOptions().length}</Link>
                                  </li>
                                )}
                              </>
                            ) : (
                              getPageOptions()
                                .filter(item => 
                                  item >= getState().pagination.pageIndex - 4 && 
                                  item <= getState().pagination.pageIndex + 4
                                )
                                .map((item, key) => (
                                  <li key={key} className={`paginate_button page-item ${getState().pagination.pageIndex === item ? "active " : ""}`}>
                                    <Link to="#" className={`page-link ${getState().pagination.pageIndex === item ? " customDynamicBg active" : ""}`} onClick={() => setPageIndex(item)}>{item + 1}</Link>
                                  </li>
                                ))
                            )}
                          <li className={`paginate_button page-item next ${!getCanNextPage() ? "disabled" : ""}`}>
                            <Link to="#" className="page-link" onClick={nextPage}><i className="mdi mdi-chevron-right"></i></Link>
                          </li>
                        </ul>
                      </div>
                    </Col>
                  </Row>
                )
              }
            </CardBody>
          </Card>
        </Col>
      </Row>
  {/* Modal Comments */}
      <CommentsModal 
        isModalOpen={isCommentsModalOpen}
        linkedEntityId={commentLinkedEntityId}
        linkedEntityModel={linkedEntityModel}
        setModalOpen={{setIsCommentsModalOpen}}
      />

  {/* Modal Documents */}
      <DocumentsModal 
        documents={documentsModalValues}
        isModalOpen={isDocumentsModalOpen}
        setModalOpen={{setIsDocumentsModalOpen}}
        filterConfig={filterConfig}
      />

{/* Modal Manage Columns */}
      <Modal isOpen={isColumnModalOpen} toggle={() => setIsColumnModalOpen(false)} centered={true} data-cy={"table-modal-manage-columns"}>
        <ModalHeader toggle={() => setIsColumnModalOpen(false)}>{t('manageColumns')}</ModalHeader>
        <ModalBody>
          <ul style={{ listStyleType: 'none', padding: 0 }}>
            {columnConfig.map(column => {
              if(!column.notInTab) {
                return (
                  <li key={column.key} style={{ display: 'flex', alignItems: 'center', lineHeight: '1.5em', marginBottom: '10px' }}>
                    <input
                    className="form-check-input"
                      type="checkbox"
                      id={"projColumnModalCheckbox"+suffixId}
                      checked={columnVisibility[column.key]}
                      onClick={() => handleColumnToggle(column.key)}
                      onChange={() => console.log()}
                      style={{ marginRight: '10px', transform: 'scale(1.5)' }}
                      data-cy={"table-modal-manage-columns-checkbox-"+ column.key}
                    />
                    <label style={{ margin: 0, fontSize: '1.2em' }}>{t(column.key)}</label>
                  </li>
                )
              }
            })}
          </ul>
        </ModalBody>
      </Modal>

{/* Modal Import File */}
      <Modal isOpen={isImportModalOpen} toggle={() => setIsImportModalOpen(false)} centered={true}>
        <ModalHeader toggle={() => setIsImportModalOpen(false)}>
          {t('importFile')}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">{t('selectFile')}</label>
            <Input
              type="file"
              className="form-control"
              onChange={(e) => {
                // Handle file upload here 
                const file = e.target.files[0];
                if (file) {
                  setFile(file);
                  // File type validation example
                  if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                      file.type === 'application/vnd.ms-excel' ||
                      file.type === 'text/csv') {
                  } else {
                    toast.error(t('invalidFileType'));
                  }
                }
              }}
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            />
            <small className="form-text text-muted">
              {t('acceptedFileTypes')}: .xlsx, .xls, .csv
            </small>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <Button color="secondary" onClick={() => setIsImportModalOpen(false)}>
              {t('cancel')}
            </Button>
            <Button color="primary" onClick={() => {
              handleUploadDocument(file);
              setIsImportModalOpen(false);
            }}>
              {t('import')}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <DeleteModal
        show={isDeleteModalOpen}
        onDeleteClick={() => {
          handleDelete(itemToDelete);
          setIsDeleteModalOpen(false);
        }}
        onCloseClick={() => setIsDeleteModalOpen(false)}
        customMessage={customDeleteMessage}
      />
      <DeleteModal
        show={isBulkDeleteModalOpen}
        onDeleteClick={handleBulkDelete}
        onCloseClick={() => setIsBulkDeleteModalOpen(!isBulkDeleteModalOpen)}
        multiple={true}
        customMessage={(selectedRows && selectedRows.length > 1)?customBulkDeleteMessage:customDeleteMessage}
      />
      <DuplicateModal
        show={isDuplicateModalOpen}
        setModalOpen={setIsDuplicateModalOpen}
        config={itemToDuplicate}
      />
      <IconListCell
        isOpen={isIconModalOpen}
        toggle={() => setIsIconModalOpen(!isIconModalOpen)}
        data={iconModalData}
        modalTitle={modalTitle}
      />
      <IconFormCell
        isOpen={isIconFormModalOpen}
        toggle={() => setIsIconFormModalOpen(!isIconFormModalOpen)}
        data={iconFormModalData}
        modalTitle={modalFormTitle}
        fields={iconFormFields}
      />
      <IconAddListCell
        isOpen={isIconAddModalOpen}
        toggle={() => setIsIconAddModalOpen(!isIconAddModalOpen)}
        data={iconAddModalData}
        modalTitle={modalAddTitle}
        preId={idSelectedModal}
        addFunction={(columnSelectedModal && columnSelectedModal.addFunction)?columnSelectedModal.addFunction:null}
        editFunction={(columnSelectedModal && columnSelectedModal.editFunction)?columnSelectedModal.editFunction:null}
        deleteFunction={(columnSelectedModal && columnSelectedModal.deleteFunction)?columnSelectedModal.deleteFunction:null}
        duplicateFunction={(columnSelectedModal && columnSelectedModal.duplicateFunction)?columnSelectedModal.duplicateFunction:null}
        columnConfig={(columnSelectedModal && columnSelectedModal.columnConfig)?columnSelectedModal.columnConfig:[]}
        fields={(columnSelectedModal && columnSelectedModal.fields)?columnSelectedModal.fields:[]}
      />
      <DynamicFormModal
        show={isEditModalOpen}
        onClose={() => setIsEditModalOpen(!isEditModalOpen)}
        initialValues={initialValuesEditPopup}
        fields={editPopupFields}
        editFunction={editFunction}
      />
    </Fragment>
  );
};

export default TableContainer;
